import type { ApiQuery, ApiResponse } from '../../api/contract';
import type { ApiClient } from '../../api/http';
import type { DashboardRepository } from './repository';
import type {
  DashboardFilters,
  DashboardMeta,
  DashboardRanking,
  DashboardSummary,
  DashboardTrends,
} from './types';

type SummaryResponse = ApiResponse<'/api/v1/dashboard/summary', 'get'>;
type TrendsResponse = ApiResponse<'/api/v1/dashboard/trends', 'get'>;
type RankingResponse = ApiResponse<'/api/v1/dashboard/top-games', 'get'>;
type DashboardQuery = ApiQuery<'/api/v1/dashboard/summary', 'get'>;

export function createRealDashboardRepository(
  apiClient: ApiClient,
  accessToken: string | null,
): DashboardRepository {
  const auth = accessToken === null ? {} : { accessToken };

  return {
    async getSummary(filters, signal) {
      const result = await apiClient.get<SummaryResponse>('/api/v1/dashboard/summary', {
        ...auth,
        query: toDashboardQuery(filters),
        signal,
      });
      return mapSummary(result.data, result.requestId);
    },
    async getTrends(filters, signal) {
      const result = await apiClient.get<TrendsResponse>('/api/v1/dashboard/trends', {
        ...auth,
        query: toDashboardQuery(filters),
        signal,
      });
      return mapTrends(result.data, result.requestId);
    },
    async getTopGames(filters, signal) {
      const result = await apiClient.get<RankingResponse>('/api/v1/dashboard/top-games', {
        ...auth,
        query: toDashboardQuery(filters),
        signal,
      });
      return mapRanking(result.data, result.requestId);
    },
    async getTopAffiliates(filters, signal) {
      const result = await apiClient.get<RankingResponse>('/api/v1/dashboard/top-affiliates', {
        ...auth,
        query: toDashboardQuery(filters),
        signal,
      });
      return mapRanking(result.data, result.requestId);
    },
  };
}

function toDashboardQuery(filters: DashboardFilters): DashboardQuery {
  return {
    from: filters.from,
    to: filters.to,
    timezone: filters.timezone,
    timeBasis: filters.timeBasis,
    partnerId: filters.partnerId,
    agentId: filters.agentId,
    gameTypeId: filters.gameTypeId,
    gameId: filters.gameId,
    currency: filters.currency,
    status: filters.status,
  };
}

function mapSummary(response: SummaryResponse, requestId: string | null): DashboardSummary {
  return {
    transactionCount: response.data.transactionCount,
    calculatedTransactionCount: response.data.calculatedTransactionCount,
    rejectedTransactionCount: response.data.rejectedTransactionCount,
    failedTransactionCount: response.data.failedTransactionCount,
    activeUserCount: response.data.activeUserCount,
    registrationCount: response.data.registrationCount,
    withdrawalExceptionCount: response.data.withdrawalExceptionCount,
    currencies: response.data.currencies.map((currency) => ({ ...currency })),
    apiHealth: null,
    meta: mapMeta(response.meta),
    requestId,
  };
}

function mapTrends(response: TrendsResponse, requestId: string | null): DashboardTrends {
  return {
    points: response.data.map((point) => ({ ...point })),
    grain: response.grain,
    meta: mapMeta(response.meta),
    requestId,
  };
}

function mapRanking(response: RankingResponse, requestId: string | null): DashboardRanking {
  return {
    rows: response.data.map((row) => ({ ...row })),
    meta: mapMeta(response.meta),
    requestId,
  };
}

function mapMeta(meta: SummaryResponse['meta']): DashboardMeta {
  return {
    snapshotId: meta.snapshotId,
    reportingAsOf: meta.reportingAsOf,
    refreshedAt: meta.refreshedAt,
    stale: meta.stale,
    timezone: meta.timezone,
    timeBasis: meta.timeBasis,
    normalizedFilters: { ...meta.normalizedFilters },
  };
}
